import type { ErrorInfo, FC, MouseEventHandler, ReactNode } from "react"
import React, { Component, useState, useLayoutEffect } from "react"

import { AnimatePresence, motion } from "framer-motion"
import { createPortal } from "react-dom"
import type { FlattenSimpleInterpolation } from "styled-components"
import styled, { css } from "styled-components"

const Overlay = styled(motion.span)<{ css?: FlattenSimpleInterpolation }>`
  ${({ css }) => css}
`

const defaultModalCss = css`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  justify-content: center;
  align-items: flex-end;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9000;
  background: var(--blue-dark-transparent);
  overflow: hidden;

  @media (min-width: 640px) {
    align-items: center;
  }

  & > div {
    margin: 0;
    width: 100%;
    max-width: 100%;
    background: #ffffff;
    filter: drop-shadow(0px 10px 20px rgba(0, 0, 0, 0.16));
    border-radius: 24px 24px 0 0;
    overflow: hidden;

    @media (min-width: 640px) {
      margin: auto;
      width: calc(100% - 40px);
      max-width: 900px;
      border-radius: 8px;
    }
  }
`

export type ModalProps = {
  onClose: MouseEventHandler<HTMLSpanElement>
  children: React.ReactNode | React.ReactNode[]
  modalId?: string
  css?: FlattenSimpleInterpolation
  modalStyle?: React.CSSProperties
}
export const Modal: FC<ModalProps> = ({
  onClose,
  children,
  modalId = `default-modal`,
  css = defaultModalCss,
  modalStyle,
}) => {
  return createPortal(
    <AnimatePresence mode="wait">
      <Overlay
        css={css}
        className="modal-underlay"
        data-testid="modal-underlay"
        key={`${modalId}-underlay`}
        id={`${modalId}-underlay`}
        initial={{ opacity: 0 }}
        animate={{
          opacity: 1,
          transition: { duration: 0.2 },
        }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          onClick={(e) => e.stopPropagation()}
          key={`${modalId}-card`}
          id={`${modalId}-card`}
          initial={{ translateY: `20vh` }}
          animate={{ translateY: `0%`, transition: { duration: 0.3 } }}
          exit={{ translateY: `-50vh` }}
          data-testid="modal-card"
          style={modalStyle}
        >
          <ErrorBoundary>{children}</ErrorBoundary>
        </motion.div>
      </Overlay>
    </AnimatePresence>,
    document.body,
  )
}

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  }

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error(`Uncaught error:`, error, errorInfo)
  }

  public render(): ReactNode {
    if (this.state.hasError) {
      return (
        <article className="modal-error-article">
          <h1 className="modal-error-title">Sorry.. there was an error</h1>
          <pre className="modal-error-pre">
            {JSON.stringify(this.state.error, null, 2)}
          </pre>
        </article>
      )
    }

    return this.props.children
  }
}
